import { supabase } from "@/integrations/supabase/client";
import posthog from "posthog-js";
import * as Sentry from "@sentry/react";
import { getCorrelationId } from "@/lib/correlation";

const SESSION_KEY = "inquira_session_id";
const REF_KEY = "inquira_ref_slug";

/**
 * Watches outbound PostHog / Sentry ingest requests and reports failures to the
 * backend (which logs them and alerts admins on repeated failures). Installed
 * once, patches window.fetch non-destructively.
 */
let monitorInstalled = false;
const reportedRecently = new Map<string, number>();

function classifyUrl(url: string): "posthog" | "sentry" | null {
  if (/i\.posthog\.com|posthog\.com\/(i|e|s|batch|decide|flags)/.test(url)) return "posthog";
  if (/ingest\.(us|de)\.sentry\.io|sentry\.io\/api\/\d+\/envelope/.test(url)) return "sentry";
  return null;
}

async function reportFailure(source: "posthog" | "sentry", url: string, statusCode?: number, detail?: string) {
  const key = `${source}:${statusCode ?? "net"}`;
  const now = Date.now();
  const last = reportedRecently.get(key) ?? 0;
  if (now - last < 60_000) return; // throttle: at most 1 report/minute per kind
  reportedRecently.set(key, now);
  try {
    const { logIntegrationFailure } = await import("@/lib/integration-health.functions");
    await logIntegrationFailure({
      data: {
        source,
        kind: "request_failed",
        statusCode,
        detail: detail?.slice(0, 2000),
        url: url.slice(0, 500),
        environment: import.meta.env.MODE,
      },
    });
  } catch {
    /* never break the app for monitoring */
  }
}

export function installAnalyticsFailureMonitor(): void {
  if (monitorInstalled || typeof window === "undefined" || typeof window.fetch !== "function") return;
  monitorInstalled = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const url =
      typeof input === "string" ? input : input instanceof URL ? input.toString() : (input as Request).url ?? "";
    const source = classifyUrl(url);
    try {
      const res = await originalFetch(input as never, init);
      if (source && !res.ok) {
        console.error(`[analytics] ${source} request failed with ${res.status}`, url);
        void reportFailure(source, url, res.status, `HTTP ${res.status} from ${source} ingest endpoint`);
      }
      return res;
    } catch (e) {
      if (source) {
        const detail = e instanceof Error ? e.message : String(e);
        console.error(`[analytics] ${source} request threw`, detail);
        void reportFailure(source, url, undefined, detail);
      }
      throw e;
    }
  };
}

let posthogInited = false;
export function initPosthog(): void {
  if (posthogInited || typeof window === "undefined") return;
  const key = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
  const host = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://us.i.posthog.com";
  if (!key) {
    console.error("[analytics] VITE_POSTHOG_KEY missing — PostHog disabled");
    return;
  }
  installAnalyticsFailureMonitor();
  try {
    posthog.init(key, {
      api_host: host,
      person_profiles: "identified_only",
      capture_pageview: true,
      capture_pageleave: true,
    });
    posthogInited = true;
  } catch (e) {
    console.error("[analytics] PostHog init failed", e);
    void reportFailure("posthog", host, undefined, e instanceof Error ? e.message : "init failed");
  }
}

let sentryInited = false;
export function initSentry(): void {
  if (sentryInited || typeof window === "undefined") return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    console.error("[analytics] VITE_SENTRY_DSN missing — Sentry disabled");
    return;
  }
  installAnalyticsFailureMonitor();
  try {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: (import.meta.env.VITE_SENTRY_RELEASE as string | undefined) ?? undefined,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 1.0,
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    });
    sentryInited = true;
  } catch (e) {
    console.error("[analytics] Sentry init failed", e);
    void reportFailure("sentry", dsn, undefined, e instanceof Error ? e.message : "init failed");
  }
}

export function isPosthogReady(): boolean {
  return posthogInited;
}

export function isSentryReady(): boolean {
  return sentryInited;
}

/** Fires a deliberate Sentry exception (admin health check). Returns event id. */
export function sendSentryTestException(): string | null {
  if (!sentryInited) return null;
  return Sentry.captureException(new Error("Inquira analytics health check: test exception")) ?? null;
}

/**
 * Post-deploy smoke test: visiting `?sentry_smoke=1` on the published site
 * captures a deliberate exception and exposes its event id on `window` so the
 * deploy verifier can confirm the stack trace was symbolicated in Sentry.
 */
export function maybeFireSentrySmokeTest(): void {
  if (typeof window === "undefined") return;
  if (new URLSearchParams(window.location.search).get("sentry_smoke") !== "1") return;
  if (!sentryInited) {
    (window as unknown as Record<string, unknown>).__sentrySmokeError = "Sentry SDK not initialized";
    return;
  }
  const id = Sentry.captureException(new Error("Inquira sourcemap smoke test"));
  (window as unknown as Record<string, unknown>).__sentrySmokeEventId = id ?? null;
  void Sentry.flush(5000);
}

export function identifySentry(userId: string, email?: string | null): void {
  if (!sentryInited) return;
  Sentry.setUser({ id: userId, email: email ?? undefined });
}

export function resetSentry(): void {
  if (!sentryInited) return;
  Sentry.setUser(null);
}

export function identifyPosthog(userId: string, props: Record<string, unknown> = {}): void {
  if (!posthogInited) return;
  posthog.identify(userId, props);
}

export function resetPosthog(): void {
  if (!posthogInited) return;
  posthog.reset();
}

function getSessionId(): string {
  if (typeof window === "undefined") return "";
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export async function track(
  event: string,
  properties: Record<string, unknown> = {}
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("analytics_events").insert({
      user_id: user?.id ?? null,
      session_id: getSessionId(),
      event_name: event.slice(0, 100),
      // correlation_id ties client events to the billing webhook logs.
      properties: { correlation_id: getCorrelationId(), ...properties } as never,
    });
    if (posthogInited) posthog.capture(event, { correlation_id: getCorrelationId(), ...properties });
  } catch {
    // swallow; analytics must never break the app
  }
}

/** Capture ?ref=slug from URL, persist for later attribution. */
export interface EntitlementTelemetry {
  tier: string;
  premium_access: boolean;
  trial_status?: string | null;
  store_platform?: string | null;
  store_status?: string | null;
  /** Apple/Google verified expiresDate — never client-computed. */
  store_expires_at?: string | null;
  scans_remaining?: number | null;
  scan_access: boolean;
  solve_access: boolean;
  [key: string]: unknown;
}

/** Last snapshot, so we only emit when entitlement state actually changes. */
let lastEntitlementFingerprint: string | null = null;

/**
 * Emits entitlement state to PostHog (event + person props) and Sentry (tags +
 * breadcrumb) so production gating bugs can be debugged from either tool.
 */
export function trackEntitlementState(snapshot: EntitlementTelemetry, reason = "sync"): void {
  const fingerprint = JSON.stringify(snapshot);
  if (fingerprint === lastEntitlementFingerprint && reason === "sync") return;
  lastEntitlementFingerprint = fingerprint;
  const payload = { ...snapshot, reason, correlation_id: getCorrelationId() };
  try {
    if (posthogInited) {
      posthog.capture("entitlement_state_changed", payload);
      posthog.setPersonPropertiesForFlags?.({ tier: String(snapshot.tier) });
    }
    if (sentryInited) {
      Sentry.setTag("entitlement_tier", String(snapshot.tier));
      Sentry.setTag("premium_access", String(snapshot.premium_access));
      if (snapshot.store_expires_at) Sentry.setTag("store_expires_at", String(snapshot.store_expires_at));
      Sentry.addBreadcrumb({ category: "entitlement", level: "info", message: "entitlement_state", data: payload });
    }
  } catch {
    /* telemetry must never break gating */
  }
  void track("entitlement_state_changed", payload);
}

/** Emits a single gating decision (allowed/blocked) for a feature. */
export function trackGatingDecision(
  feature: string,
  allowed: boolean,
  context: Record<string, unknown> = {},
): void {
  const payload = { feature, allowed, correlation_id: getCorrelationId(), ...context };
  try {
    if (posthogInited) posthog.capture("gating_decision", payload);
    if (sentryInited) {
      Sentry.addBreadcrumb({
        category: "entitlement",
        level: allowed ? "info" : "warning",
        message: `gate:${feature}:${allowed ? "allow" : "block"}`,
        data: payload,
      });
    }
  } catch {
    /* noop */
  }
}

export interface LogoRenderTelemetry {
  ok: boolean;
  /** Resolved src (hashed /assets/... URL) or "inline-data-uri" when fallback. */
  src: string;
  reason?: string;
  natural_width?: number;
  natural_height?: number;
  using_fallback?: boolean;
}

export interface PlatformSegment {
  /** Where the app is running: native shell, installed PWA, or plain browser. */
  app_build: "android-native" | "ios-native" | "pwa" | "web" | "ssr";
  os: "android" | "ios" | "ipados" | "macos" | "windows" | "other" | "unknown";
  os_version: string;
  device_type: "phone" | "tablet" | "desktop" | "unknown";
  standalone: boolean;
}

/**
 * Device/OS segmentation attached to asset telemetry so a rendering bug that
 * only happens in the Google Play WebView (or only in the iOS shell, or only in
 * the installed PWA) is separable in PostHog/Sentry instead of averaged away.
 */
export function getPlatformSegment(): PlatformSegment {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return { app_build: "ssr", os: "unknown", os_version: "", device_type: "unknown", standalone: false };
  }
  const ua = navigator.userAgent;
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPod/i.test(ua);
  const isIPad = /iPad/i.test(ua) || (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1);
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;
  // Capacitor injects a global bridge in the native shells.
  const isCapacitor = Boolean((window as unknown as { Capacitor?: unknown }).Capacitor);

  let os: PlatformSegment["os"] = "other";
  if (isAndroid) os = "android";
  else if (isIOS) os = "ios";
  else if (isIPad) os = "ipados";
  else if (/Mac OS X/.test(ua)) os = "macos";
  else if (/Windows/.test(ua)) os = "windows";

  const version =
    ua.match(/Android\s([\d.]+)/)?.[1] ??
    ua.match(/(?:iPhone )?OS\s([\d_]+)/)?.[1]?.replace(/_/g, ".") ??
    "";

  let app_build: PlatformSegment["app_build"] = "web";
  if (isCapacitor) app_build = isAndroid ? "android-native" : "ios-native";
  else if (standalone) app_build = "pwa";

  const device_type: PlatformSegment["device_type"] =
    isIPad || (isAndroid && !/Mobile/.test(ua))
      ? "tablet"
      : isAndroid || isIOS
        ? "phone"
        : os === "macos" || os === "windows"
          ? "desktop"
          : "unknown";

  return { app_build, os, os_version: version, device_type, standalone };
}

/**
 * Emits brand-logo render outcomes so a broken image in a shipped store build is
 * visible in PostHog/Sentry with the exact src that failed. Failures also land in
 * analytics_events; successes stay out of the DB to keep the write volume down.
 */
export function trackLogoRender(info: LogoRenderTelemetry): void {
  const event = info.ok ? "logo_render_success" : "logo_render_failure";
  // Long data URIs are useless in dashboards — record a stable label instead.
  const segment = getPlatformSegment();
  const payload = {
    ...info,
    src: info.src.startsWith("data:") ? "inline-data-uri" : info.src.slice(0, 300),
    platform: typeof navigator === "undefined" ? "ssr" : navigator.userAgent.slice(0, 200),
    ...segment,
  };
  try {
    if (posthogInited) posthog.capture(event, payload);
    if (sentryInited) {
      Sentry.setTag("app_build", segment.app_build);
      Sentry.setTag("device_os", segment.os);
      Sentry.setTag("device_type", segment.device_type);
      Sentry.addBreadcrumb({
        category: "asset",
        level: info.ok ? "info" : "error",
        message: event,
        data: payload,
      });
      if (!info.ok) Sentry.captureMessage("Inquira logo failed to render", { level: "error", extra: payload });
    }
  } catch {
    /* telemetry must never break rendering */
  }
  if (!info.ok) void track(event, payload);
}



export function captureReferralSlug(): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");
  if (ref && /^[a-z0-9-]{3,30}$/.test(ref.toLowerCase())) {
    localStorage.setItem(REF_KEY, ref.toLowerCase());
  }
}

/** After sign-in, attach the ref slug to the profile if unset. */
export async function attachReferralSlugIfPresent(userId: string): Promise<void> {
  if (typeof window === "undefined") return;
  const slug = localStorage.getItem(REF_KEY);
  if (!slug) return;
  try {
    const { data: profile } = await supabase
      .from("profiles")
      .select("attributed_affiliate_slug")
      .eq("user_id", userId)
      .maybeSingle();
    const p = profile as { attributed_affiliate_slug?: string | null } | null;
    if (p && !p.attributed_affiliate_slug) {
      await (supabase.from("profiles") as never as {
        update: (v: Record<string, unknown>) => { eq: (c: string, v: string) => Promise<unknown> };
      })
        .update({ attributed_affiliate_slug: slug })
        .eq("user_id", userId);
    }
    localStorage.removeItem(REF_KEY);
  } catch {
    /* noop */
  }
}